//src/components/ProductsPage/ProductsPage.jsx

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../UI/Card";
import Button from "../UI/Button";
import ProductFormModal from "./ProductFormModal";
import { authFetch } from "../api/api";
import { useTranslation } from "react-i18next";



const BASE_URL = process.env.REACT_APP_DJANGO_BACKEND;

const API_URL = `${BASE_URL}/api/products/`;
const CITIES_URL = `${BASE_URL}/api/cities/`;
const SUPPLIERS_URL = `${BASE_URL}/api/suppliers/suppliers/`;

const emptyProduct = {
  name_en: "",
  name_cn: "",
  brand: false,
  composition: "",
  image: null,
  purchase_price: "",
  standard_price: "",
  items_per_package: "",
  total_items: "",
  gross_weight_per_package: "",
  total_gross_weight: "",
  net_weight_per_package: "",
  total_net_weight: "",
  width: "",
  length: "",
  height: "",
  package_volume_calc: "",
  production_city: "",
  production_city_text: "",
  supplier_id: "",
};


function ProductImage({ src, alt, fallback }) {
  const [failed, setFailed] = React.useState(false);

  const url = /^https?:\/\//i.test(src) ? src : `${BASE_URL}${src}`;

  if (failed) {
    return (
      <div className="w-24 h-24 flex items-center justify-center bg-gray-200 text-gray-500 rounded text-xs text-center">
        {fallback}
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={alt}
      className="w-24 h-24 object-cover rounded"
      onError={() => setFailed(true)}
    />
  );
}



const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [cities, setCities] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState("");
  const [editingProduct, setEditingProduct] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState([]);
  const navigate = useNavigate(); 

  const { t } = useTranslation("ProductsPage");

  const toggleExpand = (id) => {
    setExpandedProducts((prev) =>
      prev.includes(id)
        ? prev.filter((pid) => pid !== id)
        : [...prev, id]
    );
  };

  useEffect(() => {
    fetchProducts();
    fetchCities();
    fetchSuppliers();
  }, []);

  const fetchProducts = async () => {
    try {
    const res = await authFetch(API_URL);
    const data = await res.json();
    setProducts(data);
    } catch (err) {
      console.error("Ошибка при загрузке продуктов:", err);
    }
  };

  const fetchCities = async () => {
    try {
      const res = await authFetch(CITIES_URL);
      const data = await res.json();
      setCities(data || []);
    } catch (err) {
      console.error("Ошибка при загрузке городов:", err);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await authFetch(SUPPLIERS_URL);
      const data = await res.json();
      setSuppliers(data || []);
    } catch (err) {
      console.error("Ошибка при загрузке поставщиков:", err);
    }
  };

  const filteredProducts = products.filter(
    (p) =>
      p.name_en?.toLowerCase().includes(search.toLowerCase()) ||
      p.name_cn?.toLowerCase().includes(search.toLowerCase())
  );

  const openForm = (product = null) => {
  setEditingProduct(
    product
      ? {
          ...product,
          supplier_id: product.supplier?.id || "", 
          production_city_text: "",
        }
      : { ...emptyProduct }
  );
  setFormOpen(true);
};

  const closeForm = () => {
    setEditingProduct(null);
    setFormOpen(false);
  };

  const handleSupplierChange = (index, value) => {
    const newSuppliers = [...editingProduct.suppliers];
    newSuppliers[index] = value;
    setEditingProduct({ ...editingProduct, suppliers: newSuppliers });
  };

  const addSupplier = () => {
    setEditingProduct({
      ...editingProduct,
      suppliers: [...(editingProduct.suppliers || []), ""],
    });
  };

  const removeSupplier = (index) => {
    const newSuppliers = [...editingProduct.suppliers];
    newSuppliers.splice(index, 1);
    setEditingProduct({ ...editingProduct, suppliers: newSuppliers });
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  try {
    const formData = new FormData();
    const productData = { ...editingProduct };

    // город производства
    if (productData.production_city_text?.trim()) {
      formData.append("production_city_text", productData.production_city_text);
    } else if (productData.production_city) {
      formData.append("production_city", productData.production_city);
    }

    Object.keys(productData).forEach((key) => {
      // не отправляем лишнее
      if (["id", "production_city", "production_city_text"].includes(key)) {
        return;
      }

      // suppliers
      if (key === "suppliers") {
        (productData.suppliers || []).forEach((id) => {
          if (id) {
            formData.append("supplier_id", id);
          }
        });
        return;
      }

      // image только если выбран новый файл
      if (key === "image") {
        if (productData.image instanceof File) {
          formData.append("image", productData.image);
        }
        return;
      }

      // остальные поля только если не пустые
      if (
        productData[key] !== null &&
        productData[key] !== undefined &&
        productData[key] !== ""
      ) {
        formData.append(key, productData[key]);
      }
    });

    if (productData.id) {
      await authFetch(`${API_URL}${productData.id}/`, {
        method: "PATCH",
        body: formData,
      });
    } else {
        await authFetch(API_URL, {
        method: "POST",
        body: formData,
      });
    }

    closeForm();
    fetchProducts();

  } catch (err) {
    console.error("Ошибка сервера:", err.response?.data || err);
  }
};

  const handleDelete = async (product) => {
    if (!window.confirm(t("confirm_delete", {name: product.name_en}))) return;
    try {
    await authFetch(`${API_URL}${product.id}/`, {
      method: "DELETE",
    });
      fetchProducts();
    } catch (err) {
      console.error("Ошибка при удалении продукта:", err);
    }
  };

  // Height of fixed TopBar assumed to be 64px (h-16), adjust if different
  return (
    <div className="pt-20 px-4 pb-8 sm:px-8">
      <h1 className="text-2xl font-bold mb-4">{t("products")}</h1>

      <div className="mb-4 flex flex-col sm:flex-row justify-between items-center gap-2">
        <input
          type="text"
          placeholder={t("search_products")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="p-2 border rounded w-full sm:w-1/2"
        />
        <Button
          variant="primary"
          onClick={() => openForm()}
        >
          {t("add_product")}
        </Button>
      </div>

      {/* --- Новый рендеринг списка продуктов --- */}
      <div className="space-y-4">
        {filteredProducts.map((product) => {
          const isExpanded = expandedProducts.includes(product.id);
          return (

            <Card
              key={product.id}
              hoverable
              className="p-4 space-y-3 cursor-pointer"
              onClick={() => navigate(`/products/${product.id}`)}
            >
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full gap-2">
                <div className="flex items-center space-x-4">

                {product.image ? (
                  <ProductImage
                    src={product.image}
                    alt={product.name_en || ""}
                    fallback={t("no_image")}
                  />
                ) : (
                  <div className="w-24 h-24 flex items-center justify-center bg-gray-200 text-gray-500 rounded text-xs text-center">
                    {t("no_image")}
                  </div>
                )}



                  <div>
                    <h2 className="text-lg font-bold flex items-center gap-2">
                      {product.name_en} / {product.name_cn}
                      <span className="ml-3 text-base font-semibold text-green-700">
                        {product.purchase_price || "-"} ¥
                      </span>
                    </h2>
                  </div>
                </div>
                {/* Action buttons moved outside clickable area for better layout and not overlapping form */}
                <div
                  className="flex space-x-2 mt-2 sm:mt-0 z-10"
                  onClick={e => e.stopPropagation()}
                >
                  <Button
                    variant="edit"
                    onClick={() => openForm(product)}
                  >
                    {t("edit")}
                  </Button>

                <Button
                  variant="danger"
                  onClick={() => handleDelete(product)}
                >
                  {t("delete")}
                </Button>
                </div>
              </div>

              
            </Card>
          );
        })}
      </div>  

        <ProductFormModal
          formOpen={formOpen}
          editingProduct={editingProduct}
          setEditingProduct={setEditingProduct}
          closeForm={closeForm}
          handleSubmit={handleSubmit}
          cities={cities}
          suppliers={suppliers}
          handleSupplierChange={handleSupplierChange}
          addSupplier={addSupplier}
          removeSupplier={removeSupplier}
        />

     
      </div>
  );
};

export default ProductsPage;
